import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes, PermissionScopes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import {
  AI_TRANSFER_SECTIONS,
  type AiConfigurationBundle,
  type AiConfigurationImportPreview,
  type AiConfigurationImportResult,
  type AiTransferSection,
  exportAiConfiguration,
  importAiConfiguration,
  previewAiConfigurationImport,
} from '../services/aiTransferService';
import styles from './AiTransferPanel.module.css';

const SECTION_LABEL_KEYS: Record<AiTransferSection, string> = {
  apiToolConnections: 'transfer.sectionApiConnections',
  apiTools: 'transfer.sectionApiTools',
  mcpServers: 'transfer.sectionMcpServers',
  mcpTools: 'transfer.sectionMcpTools',
  knowledgeDataCategories: 'transfer.sectionDataCategories',
  knowledgeDynamicTools: 'transfer.sectionDataTools',
  aiToolOrchestrators: 'transfer.sectionOrchestrators',
  useCasePolicies: 'transfer.sectionUseCases',
  promptTemplates: 'transfer.sectionPrompts',
  aiProviders: 'transfer.sectionProviders',
};

const SECTION_HINT_KEYS: Record<AiTransferSection, string> = {
  apiToolConnections: 'transfer.sectionApiConnectionsHint',
  apiTools: 'transfer.sectionApiToolsHint',
  mcpServers: 'transfer.sectionMcpServersHint',
  mcpTools: 'transfer.sectionMcpToolsHint',
  knowledgeDataCategories: 'transfer.sectionDataCategoriesHint',
  knowledgeDynamicTools: 'transfer.sectionDataToolsHint',
  aiToolOrchestrators: 'transfer.sectionOrchestratorsHint',
  useCasePolicies: 'transfer.sectionUseCasesHint',
  promptTemplates: 'transfer.sectionPromptsHint',
  aiProviders: 'transfer.sectionProvidersHint',
};

export const AiTransferPanel: React.FC = () => {
  const { t } = useTranslation(['tools', 'common']);
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canExport = hasPermission(PermissionScopes.aiConfigRead);
  const canImport = hasPermission(PermissionCodes.aiConfigManage);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedSections, setSelectedSections] = useState<AiTransferSection[]>([...AI_TRANSFER_SECTIONS]);
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMode, setImportMode] = useState<'skipExisting' | 'replaceExisting'>('skipExisting');
  const [bundleFile, setBundleFile] = useState<unknown | null>(null);
  const [bundleName, setBundleName] = useState('');
  const [preview, setPreview] = useState<AiConfigurationImportPreview | null>(null);
  const [importResult, setImportResult] = useState<AiConfigurationImportResult | null>(null);

  const allSelected = selectedSections.length === AI_TRANSFER_SECTIONS.length;

  const toggleSection = (section: AiTransferSection) => {
    setSelectedSections((current) => (
      current.includes(section)
        ? current.filter((item) => item !== section)
        : [...current, section]
    ));
  };

  const toggleAllSections = () => {
    setSelectedSections(allSelected ? [] : [...AI_TRANSFER_SECTIONS]);
  };

  const downloadBundle = (bundle: AiConfigurationBundle) => {
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    anchor.href = url;
    anchor.download = `shahagent-ai-config-${timestamp}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    if (!canExport || selectedSections.length === 0) return;
    setExporting(true);
    try {
      const bundle = await exportAiConfiguration(selectedSections);
      downloadBundle(bundle);
      showToast(t('transfer.exported'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('transfer.failedToExport'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setBundleName(file.name);
    setImportResult(null);

    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as unknown;
      setBundleFile(parsed);
      setPreview(null);
    } catch {
      setBundleFile(null);
      setBundleName('');
      showToast(t('transfer.invalidBundleFile'), 'error');
    }
  };

  const handlePreview = async () => {
    if (!bundleFile || selectedSections.length === 0) return;
    setPreviewing(true);
    try {
      const result = await previewAiConfigurationImport(bundleFile, selectedSections);
      setPreview(result);
      if (result.errors.length > 0) {
        showToast(result.errors[0], 'error');
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('transfer.failedToPreview'), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!canImport || !bundleFile || selectedSections.length === 0) return;
    setImporting(true);
    try {
      const result = await importAiConfiguration(bundleFile, importMode, selectedSections);
      setImportResult(result);
      if (result.errors.length > 0) {
        showToast(result.errors[0], 'error');
      } else {
        showToast(t('transfer.imported'), 'success');
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('transfer.failedToImport'), 'error');
    } finally {
      setImporting(false);
    }
  };

  const previewWarnings = useMemo(
    () => preview?.warnings ?? [],
    [preview],
  );

  return (
    <div className={styles.layout}>
      <section className={`company-section ${styles.panel}`}>
        <div>
          <h3 className={styles.panelTitle}>{t('transfer.chooseSections')}</h3>
          <p className={styles.panelIntro}>{t('transfer.chooseSectionsHelp')}</p>
        </div>

        <div className={styles.sectionList}>
          {AI_TRANSFER_SECTIONS.map((section) => (
            <label key={section} className={styles.sectionItem}>
              <input
                type="checkbox"
                checked={selectedSections.includes(section)}
                disabled={!canExport}
                onChange={() => toggleSection(section)}
              />
              <span>
                <strong>{t(SECTION_LABEL_KEYS[section])}</strong>
                <span>{t(SECTION_HINT_KEYS[section])}</span>
              </span>
            </label>
          ))}
        </div>

        <div className={styles.actionsRow}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!canExport}
            onClick={toggleAllSections}
          >
            {allSelected ? t('transfer.deselectAll') : t('transfer.selectAll')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canExport || exporting || selectedSections.length === 0}
            onClick={() => void handleExport()}
          >
            {exporting ? t('loading', { ns: 'common' }) : t('transfer.exportBundle')}
          </button>
        </div>

        <ul className={styles.hintList}>
          <li>{t('transfer.secretHint')}</li>
          <li>{t('transfer.dataHint')}</li>
        </ul>
      </section>

      <section className={`company-section ${styles.panel}`}>
        <div>
          <h3 className={styles.panelTitle}>{t('transfer.importTitle')}</h3>
          <p className={styles.panelIntro}>{t('transfer.importHelp')}</p>
        </div>

        <div className={styles.fileRow}>
          <input
            ref={fileInputRef}
            className={styles.fileInput}
            type="file"
            accept="application/json,.json"
            onChange={(event) => void handleFileSelected(event)}
          />
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!canExport}
            onClick={() => fileInputRef.current?.click()}
          >
            {t('transfer.chooseFile')}
          </button>
          {bundleName ? <span className={styles.fileName}>{bundleName}</span> : null}
        </div>

        <div className={styles.importModeRow}>
          <strong style={{ fontSize: 13 }}>{t('transfer.importMode')}</strong>
          <label className={styles.importModeOption}>
            <input
              type="radio"
              name="import-mode"
              checked={importMode === 'skipExisting'}
              disabled={!canImport}
              onChange={() => setImportMode('skipExisting')}
            />
            <span>
              <strong>{t('transfer.importModeSkip')}</strong>
              <div style={{ color: '#64748b', fontSize: 12 }}>{t('transfer.importModeSkipHint')}</div>
            </span>
          </label>
          <label className={styles.importModeOption}>
            <input
              type="radio"
              name="import-mode"
              checked={importMode === 'replaceExisting'}
              disabled={!canImport}
              onChange={() => setImportMode('replaceExisting')}
            />
            <span>
              <strong>{t('transfer.importModeReplace')}</strong>
              <div style={{ color: '#64748b', fontSize: 12 }}>{t('transfer.importModeReplaceHint')}</div>
            </span>
          </label>
        </div>

        <div className={styles.importActions}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!canExport || previewing || !bundleFile}
            onClick={() => void handlePreview()}
          >
            {previewing ? t('loading', { ns: 'common' }) : t('transfer.previewImport')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canImport || importing || !bundleFile}
            onClick={() => void handleImport()}
          >
            {importing ? t('loading', { ns: 'common' }) : t('transfer.runImport')}
          </button>
        </div>

        {!canImport ? (
          <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>{t('transfer.importReadOnlyHint')}</p>
        ) : null}

        {previewWarnings.length > 0 ? (
          <div className={styles.warningCard}>
            <strong>{t('transfer.previewWarnings')}</strong>
            <ul className={styles.hintList}>
              {previewWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {preview && preview.sections.length > 0 ? (
          <div className={styles.previewCard}>
            <strong>{t('transfer.previewSummary')}</strong>
            <table className={styles.previewTable}>
              <thead>
                <tr>
                  <th>{t('transfer.previewSection')}</th>
                  <th>{t('transfer.previewIncoming')}</th>
                  <th>{t('transfer.previewExisting')}</th>
                  <th>{t('transfer.previewConflicts')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.sections.map((section) => (
                  <tr key={section.section}>
                    <td>{t(SECTION_LABEL_KEYS[section.section as AiTransferSection] ?? section.section)}</td>
                    <td>{section.incomingCount}</td>
                    <td>{section.existingCount}</td>
                    <td>{section.conflictCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {importResult ? (
          <div className={styles.resultCard}>
            <strong>{t('transfer.importResult')}</strong>
            <table className={styles.previewTable}>
              <thead>
                <tr>
                  <th>{t('transfer.previewSection')}</th>
                  <th>{t('transfer.resultCreated')}</th>
                  <th>{t('transfer.resultUpdated')}</th>
                  <th>{t('transfer.resultSkipped')}</th>
                  <th>{t('transfer.resultFailed')}</th>
                </tr>
              </thead>
              <tbody>
                {importResult.sections.map((section) => (
                  <tr key={section.section}>
                    <td>{t(SECTION_LABEL_KEYS[section.section as AiTransferSection] ?? section.section)}</td>
                    <td>{section.created}</td>
                    <td>{section.updated}</td>
                    <td>{section.skipped}</td>
                    <td>{section.failed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};
